import { useRef, type CSSProperties, type Ref } from "react";
import { cn } from "@/lib/utils";

interface ColumnDividerProps {
  label: string;
  className?: string;
  style?: CSSProperties;
  nodeRef?: Ref<HTMLDivElement>;
  onDragStart: () => void;
  onDrag: (deltaPx: number) => void;
  onDragEnd?: () => void;
  onReset: () => void;
}

export function ColumnDivider({
  label,
  className,
  style,
  nodeRef,
  onDragStart,
  onDrag,
  onDragEnd,
  onReset,
}: ColumnDividerProps) {
  const origin = useRef(0);
  const ended = useRef(false);
  const finish = () => {
    if (ended.current) return;
    ended.current = true;
    document.body.style.userSelect = "";
    onDragEnd?.();
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      ref={nodeRef}
      style={style}
      className={cn("z-30 w-2 cursor-col-resize touch-none", className)}
      onPointerDown={(event) => {
        ended.current = false;
        origin.current = event.clientX;
        onDragStart();
        event.currentTarget.setPointerCapture(event.pointerId);
        document.body.style.userSelect = "none";
      }}
      onPointerMove={(event) => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
        onDrag(event.clientX - origin.current);
      }}
      onPointerUp={(event) => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
        event.currentTarget.releasePointerCapture(event.pointerId);
        finish();
      }}
      onPointerCancel={finish}
      onLostPointerCapture={finish}
      onDoubleClick={onReset}
    />
  );
}
